import 'react-phone-input-2/lib/bootstrap.css';
import './PhoneNrInput.css';

import { FormControl } from '@mui/material';
import { toPhoneNrWithCountryCode, useTranslate } from '@si/utils';
import type { CSSProperties } from 'react';
import { useId, useState } from 'react';
import PhoneInput, { type CountryData } from 'react-phone-input-2';
import { getCountries } from 'react-phone-number-input';

import { FormHelperText } from '../../FormHelperText/FormHelperText';
import { FormLabel } from '../../FormLabel/FormLabel';
import { useColors } from '../../ThemeProvider';

const LAST_COUNTRY_KEY = 'ics-ui:last-phone-country';
const RECENT_COUNTRIES_KEY = 'ics-ui:recent-phone-countries';
const DEFAULT_PREFERRED_COUNTRIES = ['no', 'se', 'dk', 'fi'];
const VALID_COUNTRIES = new Set(getCountries().map((country) => country.toLowerCase()));

function getRecentCountries(): string[] {
  try {
    const stored = window.localStorage.getItem(RECENT_COUNTRIES_KEY);
    const countries: unknown = stored
      ? JSON.parse(stored)
      : [window.localStorage.getItem(LAST_COUNTRY_KEY)];
    if (Array.isArray(countries)) {
      return [
        ...new Set(
          countries.filter(
            (country): country is string =>
              typeof country === 'string' && VALID_COUNTRIES.has(country)
          )
        ),
      ].slice(0, 5);
    }
  } catch {
    // Country selection remains available when browser storage is blocked or invalid.
  }
  return [];
}

function getPreferredCountries(): string[] {
  return [...new Set([...getRecentCountries(), ...DEFAULT_PREFERRED_COUNTRIES])];
}

function rememberCountry(country: string) {
  if (!VALID_COUNTRIES.has(country)) return;
  const countries = [country, ...getRecentCountries().filter((recent) => recent !== country)].slice(
    0,
    5
  );
  try {
    window.localStorage.setItem(RECENT_COUNTRIES_KEY, JSON.stringify(countries));
  } catch {
    // Saving the preference must not interrupt phone-number entry.
  }
}

interface Props {
  errorText?: string;
  helperText?: string;
  id?: string;
  inputWidth?: '10rem' | '15rem' | '20rem' | '25rem' | '40rem' | '100%';
  label?: string;
  onChange: (value: string) => void;
  overrideVisited?: boolean;
  required?: boolean;
  value: string;
}

export function PhoneNrInput({
  errorText = '',
  helperText = '',
  id,
  inputWidth = '100%',
  label,
  onChange,
  overrideVisited = false,
  required = false,
  value,
}: Props) {
  const { t, translate } = useTranslate();
  const generatedId = useId();
  const { error, primary, surface, surfaceHover, border, textPrimary, mode } = useColors();

  const [visited, setVisited] = useState(false);
  const [dialCode, setDialCode] = useState('');
  const [preferredCountries] = useState(getPreferredCountries);

  const textFieldId = id ?? generatedId;
  const labelId = `${textFieldId}-label`;
  const helperTextId = `${textFieldId}-helper-text`;
  const maxWidth = inputWidth === '10rem' || inputWidth === '15rem' ? '20rem' : inputWidth;

  const showError = !!errorText && (visited || overrideVisited);
  const containerStyle = {
    '--phone-surface': surface,
    '--phone-surface-hover': surfaceHover,
    '--phone-border': border,
    '--phone-text': textPrimary,
    '--phone-shadow-sm':
      mode === 'dark' ? '0 1px 2px 0 rgb(0 0 0 / 0.35)' : '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    '--phone-shadow-md':
      mode === 'dark'
        ? '0 8px 18px -8px rgb(0 0 0 / 0.75), 0 4px 8px -4px rgb(0 0 0 / 0.55)'
        : '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    '--phone-radius': '0.5rem',
  } as CSSProperties;

  return (
    <FormControl fullWidth sx={{ maxWidth }}>
      <FormLabel
        componentId={textFieldId}
        id={labelId}
        label={label ?? translate(t.phoneNr)}
        required={required}
        valid={errorText.length === 0}
      />

      <PhoneInput
        containerStyle={containerStyle}
        inputStyle={{
          borderColor: showError ? error : border,
          color: primary,
          backgroundColor: surface,
          borderRadius: '0.5rem',
          boxShadow: 'var(--phone-shadow-sm)',
        }}
        buttonStyle={{
          backgroundColor: surface,
          border: `1px solid ${border}`,
          borderRadius: '0.5rem 0 0 0.5rem',
        }}
        dropdownStyle={{
          backgroundColor: surface,
          color: textPrimary,
          border: `1px solid ${border}`,
          borderRadius: '0.5rem',
          boxShadow: 'var(--phone-shadow-md)',
        }}
        country={'no'}
        countryCodeEditable={false}
        inputProps={{
          id: textFieldId,
          name: textFieldId,
          'aria-labelledby': labelId,
          'aria-describedby': helperTextId,
        }}
        jumpCursorToEnd={false}
        onBlur={() => setVisited(true)}
        onChange={(value, data: CountryData | Record<string, never>) => {
          setDialCode(data.dialCode ?? '');
          if (value && data.countryCode) {
            rememberCountry(data.countryCode);
          }
          onChange(value && value !== data.dialCode ? `+${value}` : '');
        }}
        onMount={() => {
          const withCode = toPhoneNrWithCountryCode(value);
          if (value && withCode !== value) {
            onChange(withCode);
          }
        }}
        enableSearch
        disableSearchIcon
        searchPlaceholder={translate(t.searchCountryOrCode)}
        searchNotFound={translate(t.noCountriesFound)}
        preferredCountries={preferredCountries}
        preserveOrder={['preferredCountries']}
        value={value.startsWith('+') ? value.substring(1) : value || dialCode}
      />

      <FormHelperText
        id={helperTextId}
        showError={showError}
        text={showError ? errorText : helperText}
      />
    </FormControl>
  );
}
